import React, { useEffect, useRef } from 'react'
import { doublePressToExitHandler, subBackPress } from 'infra/back-press'
import { useFocusEffect } from '@react-navigation/native'
import { observer } from 'mobx-react'
import { locationStore } from 'store/location'
import { MapStore } from 'store/map'
import { permissionStore, PermissionType } from 'store/permission'
import { DefaultMapView } from 'ui/common/default-map-view'
import { useSafeAreaInsets } from 'infra/util'
import { SelectedPlacePopover } from 'ui/common/place-popover'
import { PlaceMarkers } from 'ui/home/place-markers'
import { openSettings } from 'react-native-permissions'
import { alertStore } from 'store/alert'
import { StackScreenProps } from '@react-navigation/stack'
import { HomeStackParamList } from 'navigation/home-stack'
import { usePlace } from 'api/hooks'
import {
  FeedbackButton,
  TopRightButtonContainer,
} from 'ui/discovery/top-button'
import { BottomButton, BottomButtonContainer } from 'ui/discovery/bottom-button'
import { ButtonText } from 'ui/common/text'
import { navigation } from 'navigation/global'
import { ButtonShadow } from 'ui/home/bottom-button-styles'
import { CategorySelectButton } from 'ui/common/category-select-button'
import { placesStore } from 'store/places'
import { placeMarkersCacheStore } from 'store/place-markers-cache-store'
import { BOTTOM_VIEW_BOTTOM_DISTANCE } from 'infra/constant'

export const HomeScreen: React.FC<
  StackScreenProps<HomeStackParamList, 'HomeScreen'>
> = observer(({ route }) => {
  const placeId = route.params?.placeId
  const { data: placeData } = usePlace(placeId)
  const mapStore = useRef(new MapStore()).current
  useFocusEffect(
    React.useCallback(() => {
      const sub = subBackPress(() => {
        if (mapStore.selectedPlace) {
          mapStore.onNonMarkerTouch()
          return true
        }
        return doublePressToExitHandler()
      })
      return () => sub.remove()
    }, [mapStore]),
  )
  useEffect(() => {
    if (placeId) return
    if (permissionStore.location === 'blocked') {
      alertStore.open({
        title: 'GOGO 필수 권한',
        body: 'GOGO 를 시작하려면 위치 권한이 필요해요.',
        buttonText: '권한 설정하러 가기',
        onPress: () => openSettings(),
      })
    } else {
      permissionStore
        .request(PermissionType.location)
        .then(() => locationStore.getLocation(false, false))
        .then((location) => {
          mapStore.focusLocation(location)
        })
        .then(() => locationStore.getLocation(true, true))
        .then((location) => {
          mapStore.focusLocation(location)
        })
        .catch((error) => {
          console.warn('[Location]', error)
        })
    }
  }, [mapStore, placeId])
  // 장소 제보 후 home 으로 navigation reset 됐을 때 처리
  useEffect(() => {
    if (!placeData) return
    // 신규 장소 데이터 반영
    placesStore.save([placeData])
    const placeFromPlacesStore = placesStore.getPlaceById(placeData.id)
    if (!placeFromPlacesStore) return
    placeMarkersCacheStore.save([placeFromPlacesStore])
    mapStore.selectPlace(placeFromPlacesStore)
    mapStore.focusLocation(placeFromPlacesStore.coord)
  }, [mapStore, placeData])
  const { top } = useSafeAreaInsets()
  return (
    <DefaultMapView
      mapStore={mapStore}
      MapViewChildren={observer(() => {
        if (!placesStore.places.length) return null
        return <PlaceMarkers mapStore={mapStore} />
      })}
      MapOverlayChildren={() => {
        return (
          <>
            <TopRightButtonContainer style={{ top }}>
              <FeedbackButton />
            </TopRightButtonContainer>
            <BottomButtonContainer
              style={{ bottom: BOTTOM_VIEW_BOTTOM_DISTANCE }}
            >
              <BottomButton
                onPress={() => navigation.navigate('SearchScreen')}
                style={{ paddingLeft: 22, ...ButtonShadow }}
              >
                <ButtonText>📌{'   '}장소 제보하기</ButtonText>
              </BottomButton>
            </BottomButtonContainer>
            <CategorySelectButton mapStore={mapStore} />
            <SelectedPlacePopover store={mapStore} />
          </>
        )
      }}
    />
  )
})
